import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { InspectionCategoryCacheService } from '../core/cache/inspection-category-cache-service';
import * as _ from 'underscore';
import { EnumCacheService } from '../core/cache/enum-cache-service';
import { InspectionCategoryDetailDto } from '../dtos/inspectionCategoryDetailDto';
import { InspectionDBService } from '../core/db/inspection-db-service';
import { TranslateService, TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { AffectingPointDto } from '../dtos/affectingPointDto';
import { FieldValueChangedEventDto } from '../dtos/fieldValueChangedEventDto';
import { MkdeAlertHolderDirective } from '../alert/mkde-alert-holder.directive';
import { CategoryConfig } from '../core/api/model/categoryConfig';
import { FlatInspectionResultDto } from '../core/api/model/flatInspectionResultDto';
import { InspectionElementDto } from '../core/api/model/inspectionElementDto';
import { InspectionStatusDto } from '../core/api/model/inspectionStatusDto';
import { InspectionStyleDto } from '../core/api/model/inspectionStyleDto';
import { PointToCategoryMappingConfig } from '../core/api/model/pointToCategoryMappingConfig';
import {
  FlatInspectionCategoryResultDto,
  InspectionCategoryDto,
  InspectionCategoryStatus,
  InspectionResultFieldValueDto,
  InspectionWithResultsDto,
  PointToCategoryMapping,
  PointToPointGroupMapping,
} from '../core/api/model/models';
import { InspectionResultType } from '../core/api/model/inspectionResultType';
import { PointToPointGroupMappingConfig } from '../core/api/model/pointToPointGroupMappingConfig';
import { InspectionResultElementType } from '../core/api/model/inspectionResultElementType';
import { InspectionStatus } from '../core/api/model/inspectionStatus';
import { PointGroupType } from '../core/api/model/pointGroupType';
import { InspectionElementFieldType } from '../core/api/model/inspectionElementFieldType';
import {
  NgbModal,
  NgbPopover,
  NgbNav,
  NgbNavItem,
  NgbNavLink,
  NgbNavLinkBase,
  NgbNavContent,
  NgbNavOutlet,
} from '@ng-bootstrap/ng-bootstrap';
import { InspectionCategoryDialogComponent } from '../inspection-category-dialog/inspection-category-dialog.component';
import { InspectionCategoryService, InspectionReasonService } from '@core/api';
import { forkJoin } from 'rxjs';
import { ConnectionStateService } from '@core/connection-state.service';
import { InspectionInformationDBService } from '@core/db/inspection-information-db-service';
import { InspectionInformationDto } from '../dtos/inspectionInformationDto';
import { ClientConfigDBService } from '@core/db/client-config-db-service';
import { NavigationComponent } from '../navigation/navigation.component';
import { InspectionHeaderComponent } from '../inspection/inspection-header.component';
import { MkdeAlertComponent } from '../alert/mkde-alert.component';
import { DetailListComponent } from './detail-list.component';
import { DetailMatrixComponent } from './detail-matrix.component';
import { I18nTextPipe } from '../core/pipes/i18n-text.pipe';
import { keysToTranslate } from '@core/util/keys-to-translate';

@Component({
  templateUrl: './detail.component.html',
  styleUrls: ['./detail.component.scss'],
  imports: [
    NavigationComponent,
    InspectionHeaderComponent,
    MkdeAlertComponent,
    NgbPopover,
    NgbNav,
    NgbNavItem,
    NgbNavLink,
    NgbNavLinkBase,
    NgbNavContent,
    DetailListComponent,
    DetailMatrixComponent,
    TranslateDirective,
    NgbNavOutlet,
    TranslatePipe,
    I18nTextPipe,
  ],
})
export class DetailComponent implements OnInit {
  protected connectionStateService = inject(ConnectionStateService);
  private route = inject(ActivatedRoute);
  private inspectionCategoryCacheService = inject(InspectionCategoryCacheService);
  private enumCacheService = inject(EnumCacheService);
  private inspectionDbService = inject(InspectionDBService);
  private inspectionInformationDbService = inject(InspectionInformationDBService);
  private translateService = inject(TranslateService);
  private alerts = inject(MkdeAlertHolderDirective);
  private modalService = inject(NgbModal);
  private inspectionCategoryService = inject(InspectionCategoryService);
  private clientConfigDbService = inject(ClientConfigDBService);
  private inspectionReasonService = inject(InspectionReasonService);
  private router = inject(Router);

  public inspection!: InspectionWithResultsDto;
  public categoryDetailsOfInspection!: InspectionCategoryDetailDto[];
  public notApplicableCategoriesDisplayed = false;
  public hideCheckboxControlledCategoriesInPdf = false;
  private allCategories!: InspectionElementDto[];
  private pointToCategoryMappingConfig: PointToCategoryMappingConfig | null = null;
  private pointToPointGroupMappingConfig: PointToPointGroupMappingConfig | null = null;
  private inspectionInformation!: InspectionInformationDto;

  public constructor() {
    keysToTranslate([
      'Die Kontrollinformation konnte nicht gespeichert werden.',
      'Die Kontrollresultate konnten nicht gespeichert werden.',
    ]);
  }

  public ngOnInit(): void {
    this.inspection = this.route.snapshot.data['inspection'] as InspectionWithResultsDto;
    this.allCategories = this.route.snapshot.data['inspectionCategories'] as InspectionElementDto[];
    this.inspectionInformation = this.route.snapshot.data[
      'inspectionInformation'
    ] as InspectionInformationDto;
    this.pointToCategoryMappingConfig = this.route.snapshot.data[
      'pointToCategoryMappingConfig'
    ] as PointToCategoryMappingConfig;
    this.pointToPointGroupMappingConfig = this.route.snapshot.data[
      'pointToPointGroupMappingConfig'
    ] as PointToPointGroupMappingConfig;

    // Set cache in case the user reloads the application on the detail view.
    this.initializeCaches();
    this.initializeClientConfig();
    this.categoryDetailsOfInspection = this.getCategoryDetails();
    this.initializeInspectionStatus();
    this.initializeInspectionResults();
    this.initializeInspectionInformation();
    this.saveInspection();
  }

  public getCategoryDetails(): InspectionCategoryDetailDto[] {
    const categories: InspectionCategoryDetailDto[] = (this.inspection.categoryResults ?? []).map(
      (inspectionCategoryResult) => ({
        categoryShortName: this.inspectionCategoryCacheService.getInspectionCategoryShortName(
          inspectionCategoryResult.categoryNumber,
        ),
        presentationType:
          this.inspectionCategoryCacheService.getInspectionCategoryPresentationType(
            inspectionCategoryResult,
          ),
        categoryResult: inspectionCategoryResult,
        categoryNumber: inspectionCategoryResult.categoryNumber ?? '',
        categoryName:
          this.inspectionCategoryCacheService.getInspectionCategoryName(inspectionCategoryResult),
        categoryInspectionReason: inspectionCategoryResult.inspectionReason,
        displayCategoryResult:
          this.inspectionCategoryCacheService.getDisplayCategoryResult(inspectionCategoryResult),
        displayInspectionDataOnTopOfProtocolPdf:
          this.inspectionCategoryCacheService.getDisplayInspectionDataOnTopOfProtocolPdf(
            inspectionCategoryResult.categoryNumber,
          ),
      }),
    );
    return _.sortBy(categories, (category: any) => category.categoryNumber);
  }

  public getCategoriesToDisplay(): InspectionCategoryDetailDto[] {
    return this.categoryDetailsOfInspection.filter(
      (c) =>
        this.notApplicableCategoriesDisplayed ||
        this.getCategoryResult(c.categoryNumber)?.inspectionResult !==
          InspectionResultType.NotApplicable,
    );
  }

  public getInspectionCategory(categoryNumber: string): InspectionElementDto | null | undefined {
    let inspectionCategory: InspectionElementDto | null | undefined = null;
    if (this.allCategories) {
      inspectionCategory = this.allCategories?.find((c) => c.number === categoryNumber);
    }
    return inspectionCategory;
  }

  public saveInspection() {
    this.inspectionDbService.putInspectionWithResults(this.inspection).subscribe(
      (success: string) => {
        console.log(success);
      },
      (err: any) => {
        console.log(err);
        this.alerts.danger(
          this.translateService.instant('Die Kontrollresultate konnten nicht gespeichert werden.'),
          err,
        );
      },
    );
  }

  public handlePointFieldValueChange(fieldValueChangedEvent: FieldValueChangedEventDto) {
    const numericValue = this.getNumericValue(fieldValueChangedEvent.pointResult?.fieldValue);
    const previousNumericValue = this.getNumericValue(fieldValueChangedEvent.previousFieldValue);
    if (
      numericValue !== null &&
      !isNaN(numericValue) &&
      (previousNumericValue === null || !(previousNumericValue > 0 && numericValue > 0))
    ) {
      const affectingPoint = this.getAffectingPoint(
        fieldValueChangedEvent.categoryNumber,
        fieldValueChangedEvent.pointResult?.inspectionElementKey,
        numericValue,
      );
      if (
        affectingPoint &&
        affectingPoint.categoryNumber &&
        affectingPoint.pointGroupNumber &&
        affectingPoint.pointNumber
      ) {
        this.handleAffectedPointGroups(affectingPoint);
        this.handleAffectedCategories(affectingPoint);
      }
    }
  }

  public saveForceDisplayOnProtocol(categoryNumber: string) {
    const categoryList =
      this.inspectionInformation.alwaysDisplayAsControlledCategoriesInProtocolPdf;
    const foundCategoryIndexes = categoryList
      ?.map((value, index) => (value === categoryNumber ? index : -1))
      .filter((index) => index !== -1);

    if (!!foundCategoryIndexes && foundCategoryIndexes.length > 0) {
      foundCategoryIndexes.forEach((categoryIndex) => {
        categoryList?.splice(categoryIndex, 1);
      });
    } else {
      categoryList?.push(categoryNumber);
    }

    this.saveInspectionInformation();
  }

  public isSaveForceDisplayOnProtocolChecked(categoryNumber: string): boolean {
    return (
      this.inspectionInformation?.alwaysDisplayAsControlledCategoriesInProtocolPdf?.includes(
        categoryNumber,
      ) ?? false
    );
  }

  public isReadOnly() {
    return this.inspection.inspectionStatus.asEnum === InspectionStatus.Entered;
  }

  public isStatusEntered(): boolean {
    return this.inspection.inspectionStatus.asEnum === InspectionStatus.Entered;
  }

  public openInspectionCategoryDialog(): void {
    forkJoin({
      inspectionCategoryResult: this.inspectionCategoryService.getInspectionCategories(),
      inspectionReasonResult: this.inspectionReasonService.getInspectionReasons(),
    }).subscribe(({ inspectionCategoryResult, inspectionReasonResult }) => {
      const modal = this.modalService.open(InspectionCategoryDialogComponent, {
        size: 'lg',
        backdrop: 'static',
        keyboard: false,
        centered: true,
      });

      modal.componentInstance.inspectionCategories =
        this.getActiveCategories(inspectionCategoryResult);
      modal.componentInstance.inspectionReasons = inspectionReasonResult;
      modal.componentInstance.inspection = this.inspection;
      modal.result.then(
        () => {
          const currentUrl = this.router.url;
          this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
            this.router.navigate([currentUrl]);
          });
        },
        () => {},
      );
    });
  }

  private getActiveCategories(inspectionCategoryResult: InspectionCategoryDto[]) {
    const categoriesOfInspection = this.inspection.categoryResults?.map(
      (categoryResult: FlatInspectionCategoryResultDto) => categoryResult.categoryNumber,
    );
    let activeCategories = inspectionCategoryResult.filter(
      (category: InspectionCategoryDto) =>
        category.status && category.status.asEnum === InspectionCategoryStatus.IcsActive,
    );
    activeCategories = activeCategories.filter(
      (category: InspectionCategoryDto) => !categoriesOfInspection?.includes(category.number),
    );
    activeCategories = activeCategories.sort((a, b) =>
      (a.number ?? '').localeCompare(b.number ?? ''),
    );
    return activeCategories;
  }

  private initializeCaches(): void {
    const categoryConfig = this.route.snapshot.data['categoryConfig'] as CategoryConfig;
    const inspectionStyles = this.route.snapshot.data['inspectionStyles'] as InspectionStyleDto[];
    const inspectionStates = this.route.snapshot.data['inspectionStates'] as InspectionStatusDto[];
    this.inspectionCategoryCacheService.setCache(categoryConfig, this.allCategories);
    this.enumCacheService.setCache(inspectionStyles, inspectionStates);
  }

  private initializeClientConfig(): void {
    this.hideCheckboxControlledCategoriesInPdf = false;
    this.clientConfigDbService.getClientConfig().subscribe(
      (config) => {
        if (config) {
          this.hideCheckboxControlledCategoriesInPdf =
            config.hideCheckboxControlledCategoriesInPdf ?? false;
        }
      },
      () => {},
    );
  }

  private initializeInspectionResults(): void {
    this.inspection.categoryResults?.forEach((category) => {
      const categoryElement = this.allCategories?.find((c) => c.number === category.categoryNumber);
      if (!category.results) {
        category.results = new Array<FlatInspectionResultDto>();
      }
      if (categoryElement) {
        this.initializeResultsForElement(category.results, categoryElement);
      }
    });
  }

  private initializeInspectionInformation(): void {
    if (!this.inspectionInformation) {
      this.inspectionInformation = new InspectionInformationDto();
      this.inspectionInformation.inspectionKey = this.inspection.inspectionKey;
      this.inspectionInformation.alwaysDisplayAsControlledCategoriesInProtocolPdf = [];
    }
    this.saveInspectionInformation();
  }

  private saveInspectionInformation(): void {
    this.inspectionInformationDbService
      .putInspectionInformation(this.inspectionInformation)
      .subscribe(
        (success: string) => {
          console.log(success);
        },
        (err: any) => {
          console.log(err);
          this.alerts.danger(
            this.translateService.instant(
              'Die Kontrollinformation konnte nicht gespeichert werden.',
            ),
            err,
          );
        },
      );
  }

  private initializeInspectionStatus(): void {
    if (this.inspection.inspectionStatus.asEnum === InspectionStatus.Planned) {
      this.inspection.inspectionStatus =
        this.enumCacheService
          .getInspectionStates()
          .find((s) => s.asEnum === InspectionStatus.Working) ?? this.inspection.inspectionStatus;
    }
  }

  private getCategoryResult(categoryNumber: string): FlatInspectionResultDto | undefined {
    const categoryResults = this.inspection.categoryResults?.find(
      (category) => category.categoryNumber === categoryNumber,
    );
    return categoryResults?.results?.find(
      (result) => result.inspectionElementKey === this.getCategoryKey(categoryNumber),
    );
  }

  private getResult(
    results: FlatInspectionResultDto[],
    inspectionElementKey: string,
  ): FlatInspectionResultDto | null {
    let result: FlatInspectionResultDto | null = null;
    if (results && results.length) {
      result = results.find((r) => r.inspectionElementKey === inspectionElementKey) ?? null;
    }

    return result;
  }

  private getCategoryKey(categoryNumber: string): string | undefined {
    return this.allCategories?.find((category) => category.number === categoryNumber)?.key;
  }

  private initializeResultsForElement(
    results: FlatInspectionResultDto[],
    inspectionElement: InspectionElementDto,
  ): void {
    if (inspectionElement.pointGroupType !== PointGroupType.SubheadWithoutDataEntry) {
      const result = this.getResult(results, inspectionElement.key);
      if (!result) {
        if (!inspectionElement.fieldType) {
          results.push(
            this.getDefaultResult(inspectionElement.key, InspectionResultType.NotDefined),
          );
        } else {
          results.push(
            this.getDefaultFieldResult(inspectionElement.key, inspectionElement.fieldType),
          );
        }
      } else if (
        result.inspectionResult === InspectionResultType.NotControlled ||
        result.inspectionResult === InspectionResultType.NotApplicable
      ) {
        this.propagateResultToChildren(results, inspectionElement, result.inspectionResult);
      }
    }
    if (inspectionElement.childElements) {
      inspectionElement.childElements.forEach((childInspectionElement) => {
        this.initializeResultsForElement(results, childInspectionElement);
      });
    }
  }

  private propagateResultToChildren(
    results: FlatInspectionResultDto[],
    inspectionElement: InspectionElementDto,
    resultType: InspectionResultType,
  ): void {
    if (inspectionElement.childElements) {
      inspectionElement.childElements.forEach((childInspectionElement) => {
        if (childInspectionElement.pointGroupType !== PointGroupType.SubheadWithoutDataEntry) {
          const existingResult = this.getResult(results, childInspectionElement.key);
          if (existingResult) {
            if (
              existingResult.inspectionResult !== resultType &&
              !childInspectionElement.fieldType
            ) {
              this.changeResultType(existingResult, resultType);
            }
          } else {
            if (!childInspectionElement.fieldType) {
              results.push(this.getDefaultResult(childInspectionElement.key, resultType));
            } else {
              results.push(
                this.getDefaultFieldResult(
                  childInspectionElement.key,
                  childInspectionElement.fieldType,
                ),
              );
            }
          }
        }
        this.propagateResultToChildren(results, childInspectionElement, resultType);
      });
    }
  }

  private getDefaultResult(
    inspectionElementKey: string,
    resultType: InspectionResultType | null,
  ): FlatInspectionResultDto {
    return {
      amountAffected: null,
      complaintRemedyDateDue: null,
      defectDescription: null,
      defectRepetition: null,
      defectSeverity: null,
      // @ts-expect-error: strict null check ignored intentionally
      fieldValue: null,
      inspectionElementKey,
      // @ts-expect-error: strict null check ignored intentionally
      inspectionResult: resultType,
      livestockOwnerFeedbackDateDue: null,
      remark: null,
    };
  }

  private getDefaultFieldResult(
    inspectionElementKey: string,
    fieldType: InspectionElementFieldType,
  ): FlatInspectionResultDto {
    const result = this.getDefaultResult(inspectionElementKey, null);
    result.fieldValue = {
      dateValue: null,
      numberValue: null,
      textValue: null,
      yesNoValue: null,
      inspectionElementFieldType: fieldType,
    };
    return result;
  }

  private changeResultType(
    result: FlatInspectionResultDto,
    newResultType: InspectionResultType,
  ): void {
    result.amountAffected = null;
    result.complaintRemedyDateDue = null;
    result.defectDescription = null;
    result.defectRepetition = null;
    result.defectSeverity = null;
    // @ts-expect-error: strict null check ignored intentionally
    result.fieldValue = null;
    result.inspectionResult = newResultType;
    result.livestockOwnerFeedbackDateDue = null;
    result.remark = null;
  }

  private getNumericValue(
    fieldValue: InspectionResultFieldValueDto | null | undefined,
  ): number | null {
    let numericValue: number | null = null;
    if (fieldValue) {
      if (
        fieldValue.inspectionElementFieldType === InspectionElementFieldType.Number &&
        fieldValue.numberValue != null
      ) {
        numericValue = fieldValue.numberValue;
      } else if (
        fieldValue.inspectionElementFieldType === InspectionElementFieldType.Text &&
        fieldValue.textValue &&
        typeof fieldValue.textValue === 'number'
      ) {
        numericValue = Number(fieldValue.textValue);
      } else if (
        fieldValue.inspectionElementFieldType === InspectionElementFieldType.YesNo &&
        fieldValue.yesNoValue != null
      ) {
        numericValue = fieldValue.yesNoValue ? 1 : 0;
      }
    }
    return numericValue;
  }

  private getAffectingPoint(
    categoryNumber: string | undefined,
    pointKey: string | null | undefined,
    value: number,
  ) {
    const affectingPoint: AffectingPointDto = {
      categoryNumber: null,
      pointGroupNumber: null,
      pointNumber: null,
      value,
    };
    const inspectionCategory = this.allCategories?.find(
      (category) => category.number === categoryNumber,
    );
    if (inspectionCategory && pointKey) {
      this.fillPathToAffectingPoint(inspectionCategory, affectingPoint, pointKey);
    }
    return affectingPoint;
  }

  private fillPathToAffectingPoint(
    inspectionElement: InspectionElementDto,
    affectingPoint: AffectingPointDto,
    pointKey: string,
  ): void {
    if (this.isPointInSubtree(inspectionElement, pointKey)) {
      if (inspectionElement.childElements) {
        inspectionElement.childElements.forEach((childElement) => {
          this.fillPathToAffectingPoint(childElement, affectingPoint, pointKey);
        });
      }
      if (
        inspectionElement.type === InspectionResultElementType.Point &&
        inspectionElement.key === pointKey
      ) {
        affectingPoint.pointNumber = inspectionElement.number;
      } else if (
        inspectionElement.type === InspectionResultElementType.PointGroup &&
        affectingPoint.pointNumber
      ) {
        if (
          inspectionElement.pointGroupType === PointGroupType.Regular ||
          !affectingPoint.pointGroupNumber
        ) {
          affectingPoint.pointGroupNumber = inspectionElement.number;
        }
      } else if (
        inspectionElement.type === InspectionResultElementType.Category &&
        affectingPoint.pointGroupNumber &&
        affectingPoint.pointNumber
      ) {
        affectingPoint.categoryNumber = inspectionElement.number;
      }
    }
  }

  private handleAffectedPointGroups(affectingPoint: AffectingPointDto): void {
    if (
      this.pointToPointGroupMappingConfig == null ||
      this.pointToPointGroupMappingConfig.pointToPointGroupMappings == null
    ) {
      return;
    }
    const affectedMappings = this.pointToPointGroupMappingConfig.pointToPointGroupMappings.filter(
      (mapping) =>
        mapping.affectingCategoryNumber === affectingPoint.categoryNumber &&
        mapping.affectingPointGroupNumber === affectingPoint.pointGroupNumber &&
        mapping.affectingPointNumber === affectingPoint.pointNumber,
    );
    if (affectedMappings == null) {
      return;
    }
    affectedMappings.forEach((mapping) => {
      const categoryResultContainer = this.inspection.categoryResults?.find(
        (result) => result.categoryNumber === mapping.affectedCategoryNumber,
      );
      const categoryDefinition = this.allCategories?.find(
        (category) => category.number === mapping.affectedCategoryNumber,
      );
      if (categoryResultContainer && categoryResultContainer.results && categoryDefinition) {
        const affectedPointGroupElement = this.getPointGroupElement(
          categoryDefinition,
          mapping.affectedPointGroupNumber ?? '',
        );
        if (affectedPointGroupElement) {
          const pointGroupResult = categoryResultContainer.results.find(
            (result) => result.inspectionElementKey === affectedPointGroupElement.key,
          );
          const affectingPointValues = this.getAffectingPointValuesForPointGroup(mapping);
          const newResultType = this.getNewResultType(affectingPointValues, affectingPoint);
          if (newResultType) {
            if (pointGroupResult && pointGroupResult.inspectionResult !== newResultType) {
              pointGroupResult.inspectionResult = newResultType;
              pointGroupResult.remark = null;
            }
            this.propagateResultToChildren(
              categoryResultContainer.results,
              affectedPointGroupElement,
              newResultType,
            );
          }
        }
      }
    });
  }

  private handleAffectedCategories(affectingPoint: AffectingPointDto): void {
    if (
      this.pointToCategoryMappingConfig == null ||
      this.pointToCategoryMappingConfig?.pointToCategoryMappings == null
    ) {
      return;
    }
    const affectedMappings = this.pointToCategoryMappingConfig?.pointToCategoryMappings.filter(
      (mapping) =>
        mapping.affectingCategoryNumber === affectingPoint.categoryNumber &&
        mapping.affectingPointGroupNumber === affectingPoint.pointGroupNumber &&
        mapping.affectingPointNumber === affectingPoint.pointNumber,
    );
    if (affectedMappings == null) {
      return;
    }
    affectedMappings.forEach((mapping) => {
      const categoryResultContainer = this.inspection.categoryResults?.find(
        (results) => results.categoryNumber === mapping.affectedCategoryNumber,
      );
      const categoryDefinition = this.allCategories?.find(
        (category) => category.number === mapping.affectedCategoryNumber,
      );
      if (categoryResultContainer && categoryResultContainer.results && categoryDefinition) {
        const categoryResult = categoryResultContainer.results.find(
          (result) => result.inspectionElementKey === categoryDefinition.key,
        );
        const affectingPointValues = this.getAffectingPointValuesForCategory(mapping);
        const newResultType = this.getNewResultType(affectingPointValues, affectingPoint);
        if (newResultType) {
          if (categoryResult && categoryResult.inspectionResult !== newResultType) {
            categoryResult.inspectionResult = newResultType;
            categoryResult.remark = null;
          }
          this.propagateResultToChildren(
            categoryResultContainer.results,
            categoryDefinition,
            newResultType,
          );
        }
      }
    });
  }

  private getNewResultType(affectingPointValues: number[], affectingPoint: AffectingPointDto) {
    let newResultType: InspectionResultType | null = null;
    if (Math.max(...affectingPointValues) <= 0) {
      newResultType = InspectionResultType.NotApplicable;
    } else if (affectingPointValues.reduce((a, b) => a + b) - (affectingPoint.value ?? 0) === 0) {
      newResultType = InspectionResultType.NotDefined;
    }
    return newResultType;
  }

  private getAffectingPointValuesForPointGroup(
    affectedPointGroupMapping: PointToPointGroupMapping,
  ): number[] {
    const affectingPointValues = new Array<number>();
    const affectingMappings =
      this.pointToPointGroupMappingConfig?.pointToPointGroupMappings?.filter(
        (m) =>
          m.affectedCategoryNumber === affectedPointGroupMapping.affectedCategoryNumber &&
          m.affectedPointGroupNumber === affectedPointGroupMapping.affectedPointGroupNumber,
      );
    if (affectingMappings == null) {
      return affectingPointValues;
    }
    affectingMappings.forEach((affectingMapping) => {
      const affectingResultContainer = this.inspection.categoryResults?.find(
        (results) => results.categoryNumber === affectingMapping.affectingCategoryNumber,
      );
      const affectingCategoryDefinition = this.allCategories?.find(
        (category) => category.number === affectingMapping.affectingCategoryNumber,
      );
      if (
        affectingResultContainer &&
        affectingResultContainer.results &&
        affectingCategoryDefinition
      ) {
        const affectingPointElement = this.getPointElement(
          affectingCategoryDefinition,
          affectingMapping.affectingPointGroupNumber,
          affectingMapping.affectingPointNumber,
        );
        const affectingPointValue = this.getAffectingPointValue(
          affectingPointElement,
          affectingResultContainer,
        );
        if (affectingPointValue !== null) {
          affectingPointValues.push(affectingPointValue);
        }
      }
    });
    return affectingPointValues;
  }

  private getAffectingPointValuesForCategory(
    affectedCategoryMapping: PointToCategoryMapping,
  ): number[] {
    const affectingPointValues = new Array<number>();
    const affectingMappings = this.pointToCategoryMappingConfig?.pointToCategoryMappings?.filter(
      (m) => m.affectedCategoryNumber === affectedCategoryMapping.affectedCategoryNumber,
    );
    if (affectingMappings == null) {
      return affectingPointValues;
    }
    affectingMappings.forEach((affectingMapping) => {
      const affectingResultContainer = this.inspection.categoryResults?.find(
        (results) => results.categoryNumber === affectingMapping.affectingCategoryNumber,
      );
      const affectingCategoryDefinition = this.allCategories?.find(
        (category) => category.number === affectingMapping.affectingCategoryNumber,
      );
      if (
        affectingResultContainer &&
        affectingResultContainer.results &&
        affectingCategoryDefinition
      ) {
        const affectingPointElement = this.getPointElement(
          affectingCategoryDefinition,
          affectingMapping.affectingPointGroupNumber,
          affectingMapping.affectingPointNumber,
        );
        const affectingPointValue = this.getAffectingPointValue(
          affectingPointElement,
          affectingResultContainer,
        );
        if (affectingPointValue !== null) {
          affectingPointValues.push(affectingPointValue);
        }
      }
    });
    return affectingPointValues;
  }

  private getAffectingPointValue(
    affectingPointElement: InspectionElementDto | null,
    affectingResultContainer: FlatInspectionCategoryResultDto,
  ): number | null {
    if (!affectingPointElement) {
      return null;
    }
    const affectingPointResult = affectingResultContainer.results?.find(
      (result) => result.inspectionElementKey === affectingPointElement.key,
    );
    if (affectingPointResult) {
      const numericValue = this.getNumericValue(affectingPointResult.fieldValue);
      if (numericValue !== null && !isNaN(numericValue)) {
        return numericValue;
      }
    }
    return null;
  }

  private getPointGroupElement(
    inspectionElement: InspectionElementDto,
    pointGroupNumber: string | null | undefined,
  ): InspectionElementDto | null {
    let pointGroupElement: InspectionElementDto | null = null;
    if (
      inspectionElement.type === InspectionResultElementType.PointGroup &&
      inspectionElement.number === pointGroupNumber
    ) {
      pointGroupElement = inspectionElement;
    } else if (inspectionElement.childElements) {
      inspectionElement.childElements.forEach((childElement) => {
        const childPointGroupElement = this.getPointGroupElement(childElement, pointGroupNumber);
        if (childPointGroupElement) {
          pointGroupElement = childPointGroupElement;
        }
      });
    }
    return pointGroupElement;
  }

  private getPointElement(
    inspectionElement: InspectionElementDto,
    pointGroupNumber: string | null | undefined,
    pointNumber: string | null | undefined,
  ): InspectionElementDto | null {
    const pointGroupElement = this.getPointGroupElement(inspectionElement, pointGroupNumber);
    if (pointGroupElement && pointGroupElement.childElements) {
      return (
        pointGroupElement.childElements.find(
          (pointElement) => pointElement.number === pointNumber,
        ) ?? null
      );
    }
    return null;
  }

  private isPointInSubtree(inspectionElement: InspectionElementDto, pointKey: string): boolean {
    let pointFound = false;
    if (inspectionElement.childElements) {
      inspectionElement.childElements.forEach(
        (childElement) =>
          (pointFound = pointFound || this.isPointInSubtree(childElement, pointKey)),
      );
    }

    if (
      inspectionElement.type === InspectionResultElementType.Point &&
      inspectionElement.key === pointKey
    ) {
      pointFound = true;
    }

    return pointFound;
  }
}
